"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Activity,
  Baby,
  Calendar,
  Heart,
  MapPin,
  MilkIcon as Cow,
  Stethoscope,
} from "lucide-react";
import { apiClient } from "@/api/api.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { coatColor, nigerianBreeds, vaccineArray } from "@/utils/items.ts";
import { reproductiveProcessInput } from "@/models/reproductiveProcess.schema.ts";
import { useAnimalUpdateRecord } from "@/hooks/useAnimalUpdateRecord";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  FormField as Controller,
} from "@/components/ui/form";

declare global {
  interface Window {
    anime: any;
  }
}

export function ReproductiveProcess() {
  const { id } = useParams();
  const formRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { data: animal, isLoading, isError, refetch } = useAnimalUpdateRecord(id);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) =>
    String(currentYear - i),
  );

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["animals"],
    mutationFn: async (data: any) => {
      try {
        const response = await apiClient.post(
          "/admin/reproductiveProcess",
          data,
        );

        return response.data;
      } catch (error) {
        throw error;
      }
    },
    onMutate: async (data: any) => {
      await queryClient.cancelQueries({ queryKey: ["animals"] });
      const previousAnimals = queryClient.getQueryData<any>(["animals"]);

      queryClient.setQueryData<any>(["animals"], (old: any) => {
        if (!old) return [];

        return old.map((animal: any) =>
          animal._id === id
            ? {
                ...animal,
                reproductiveProcess: [data],
              }
            : animal,
        );
      });

      return { previousAnimals };
    },
    onSuccess: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ["animals"] });
      toast.success("Reproductive process saved successfully");
    }, [queryClient]),
    onError: useCallback(
      (error: Error, _variables: any, context: any) => {
        if (error instanceof AxiosError) {
          toast.error(`${error.response?.data.message}`);
        }
        toast.error("Failed to save reproductive process. Your edits have been kept for retry.");
        queryClient.setQueryData(["animals"], context?.previousAnimals);
      },
      [queryClient],
    ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["animals"] });
      queryClient.invalidateQueries({ queryKey: ["allAnimals"] });
      queryClient.invalidateQueries({ queryKey: ["adminAnimal", id] });
      queryClient.invalidateQueries({ queryKey: ["SilaAnimal", id] });
    },
  });

  const [currentVaccination, setCurrentVaccination] = useState<string[]>([]);

  const form = useForm<reproductiveProcessInput>({
    mode: "onChange",
    defaultValues: {
      cowEarTag: "",
      branding: "",
      cowManagement: "",
      cowBreed: "",
      cowBirthDate: "",
      syncYear: "",
      syncMonth: "",
      dateAIET: "",
      synchRemoval: "",
      syncBatch: "",
      aiOrETType: "",
      bull: "",
      donorCow: "",
      pregnancyCheck1: "",
      finalPregnancyCheckDate: "",
      finalPregnancyResult: "",
      finalPregnancyNotes: "",
      calfBirthDate: "",
      calfBirthYear: "",
      calfBirthMonth: "",
      calfNumber: "",
      calfSex: "",
      calfCoatColor: "",
      calfBirthWeight: 0,
      calfType: "",
      gestationPeriod: 0,
      calfBirthNotes: "",
      calfDeathDate: "",
      calfDeathNotes: "",
      weaningDate: "",
      weaningWeight: 0,
      weaningLocation: "",
      tickCount: 0,
      headEvaluation: "",
      bodyType: "",
      color: "",
      muscleDevelopment: "",
      umbilicalCondition: "",
      bodyStructure: "",
      generalConformation: "",
      earTagVerified: "",
      vaccinations: [],
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, dirtyFields },
    trigger,
    reset,
    getValues,
  } = form;

  useEffect(() => {
    const process = Array.isArray(animal?.reproductiveProcess)
      ? animal.reproductiveProcess[0] : animal?.reproductiveProcess;
    if (process) {
      reset({ ...getValues(), ...process }, { keepDirtyValues: true });
      setCurrentVaccination(Array.isArray(process.vaccinations) ? process.vaccinations : []);
    }
  }, [animal, reset, getValues]);

  // Initialize with dummy data

  const formSections = [
    {
      title: "Cow Information",
      icon: <Cow className="w-5 h-5" />,
      fields: [
        "cowEarTag",
        "branding",
        "cowManagement",
        "cowBreed",
        "cowBirthDate",
      ],
    },
    {
      title: "Synchronization & Breeding",
      icon: <Calendar className="w-5 h-5" />,
      fields: [
        "syncYear",
        "syncMonth",
        "dateAIET",
        "synchRemoval",
        "syncBatch",
        "aiOrETType",
        "bull",
        "donorCow",
      ],
    },
    {
      title: "Pregnancy Tracking",
      icon: <Activity className="w-5 h-5" />,
      fields: [
        "pregnancyCheck1",
        "finalPregnancyCheckDate",
        "finalPregnancyResult",
        "finalPregnancyNotes",
      ],
    },
    {
      title: "Calf Information",
      icon: <Baby className="w-5 h-5" />,
      fields: [
        "calfBirthDate",
        "calfBirthYear",
        "calfBirthMonth",
        "calfNumber",
        "calfSex",
        "calfCoatColor",
        "calfBirthWeight",
        "calfType",
        "gestationPeriod",
        "calfBirthNotes",
        "calfDeathDate",
        "calfDeathNotes",
      ],
    },
    {
      title: "Weaning & Location",
      icon: <MapPin className="w-5 h-5" />,
      fields: ["weaningDate", "weaningWeight", "weaningLocation"],
    },

    {
      title: "Donor Animal Section",
      icon: <Heart className="w-5 h-5" />,
      fields: [
        "dgrRecipientCow",
        "donorWeight",
        "bcsD0",
        "donorCowAnimal",
        "donorBreed",
        "bullName",
        "dgrBull",
        "donorBreedSecond",
        "embryoBreed",
      ],
    },
    {
      title: "Health & Evaluation",
      icon: <Stethoscope className="w-5 h-5" />,
      fields: [
        "tickCount",
        "headEvaluation",
        "bodyType",
        "color",
        "muscleDevelopment",
        "umbilicalCondition",
        "bodyStructure",
        "generalConformation",
        "earTagVerified",
        "vaccinations",
      ],
    },
  ];

  const fieldDefinitions = {
    cowEarTag: { label: "Cow Ear Tag", type: "text", required: true },
    branding: { label: "Branding", type: "text", required: true },
    cowManagement: {
      label: "Cow Management",
      type: "select",
      required: true,
      options: ["Intensive", "Extensive", "Semi-Intensive"],
    },
    cowBreed: {
      label: "Cow Breed",
      type: "select",
      required: true,
      options: nigerianBreeds.map((breed) => breed.breed),
    },
    cowBirthDate: { label: "Cow Birth Date", type: "date", required: true },
    syncYear: {
      label: "Sync Year",
      type: "select",
      required: true,
      options: yearOptions,
    },
    syncMonth: {
      label: "Sync Month",
      type: "select",
      required: true,
      options: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
    },
    dateAIET: { label: "Date AI-ET", type: "date", required: true },
    synchRemoval: { label: "Synch Removal", type: "date", required: false },
    syncBatch: { label: "Sync Batch", type: "text", required: true },
    aiOrETType: {
      label: "AI or ET Type",
      type: "select",
      required: true,
      options: ["AI", "ET", "Natural"],
    },
    bull: { label: "Bull", type: "text", required: true },
    donorCow: { label: "Donor (Cow)", type: "text", required: false },
    dgrRecipientCow: {
      label: "DGR Recipient Cow",
      type: "text",
      required: true,
    },
    donorWeight: { label: "Weight (kg)", type: "number", required: true },
    bcsD0: {
      label: "BCS D0",
      type: "select",
      required: true,
      options: ["1.0", "1.5", "2.0", "2.5", "3.0", "3.5", "4.0", "4.5", "5.0"],
    },
    donorCowAnimal: { label: "Donor Cow", type: "text", required: true },
    donorBreed: {
      label: "Donor Breed",
      type: "select",
      required: true,
      options: nigerianBreeds.map((breed) => breed.breed),
    },
    bullName: { label: "Bull Name", type: "text", required: true },
    dgrBull: { label: "DGR Bull", type: "text", required: true },
    donorBreedSecond: {
      label: "Donor Breed",
      type: "select",
      required: true,
      options: nigerianBreeds.map((breed) => breed.breed),
    },
    embryoBreed: {
      label: "Embryo Breed",
      type: "select",
      required: true,
      options: nigerianBreeds.map((breed) => breed.breed),
    },
    pregnancyCheck1: {
      label: "Pregnancy Check 1",
      type: "select",
      required: true,
      options: ["Positive", "Negative"],
    },
    finalPregnancyCheckDate: {
      label: "Final Pregnancy Check Date",
      type: "date",
      required: true,
    },
    finalPregnancyResult: {
      label: "Final Pregnancy Result",
      type: "select",
      required: true,
      options: ["Positive", "Negative"],
    },
    finalPregnancyNotes: {
      label: "Final Pregnancy Notes",
      type: "textarea",
      required: false,
    },
    calfBirthDate: { label: "Calf Birth Date", type: "date", required: false },
    calfBirthYear: {
      label: "Calf Birth Year",
      type: "select",
      required: false,
      options: yearOptions,
    },
    calfBirthMonth: {
      label: "Calf Birth Month",
      type: "select",
      required: false,
      options: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
    },
    calfNumber: { label: "Calf Number", type: "text", required: false },
    calfSex: {
      label: "Calf Sex",
      type: "select",
      required: false,
      options: ["Male", "Female"],
    },
    calfCoatColor: {
      label: "Calf Coat Color",
      type: "select",
      required: false,
      options: coatColor.map((color) => color.coatColor),
    },
    calfBirthWeight: {
      label: "Calf Birth Weight (kg)",
      type: "number",
      required: false,
    },
    calfType: {
      label: "Calf Type",
      type: "select",
      required: false,
      options: ["Natural", "AI", "ET"],
    },
    gestationPeriod: {
      label: "Gestation Period (Days)",
      type: "number",
      required: false,
    },
    calfBirthNotes: {
      label: "Calf Birth Notes",
      type: "textarea",
      required: false,
    },
    calfDeathDate: { label: "Calf Death Date", type: "date", required: false },
    calfDeathNotes: {
      label: "Calf Death Notes",
      type: "textarea",
      required: false,
    },
    weaningDate: { label: "Weaning Date", type: "date", required: false },
    weaningWeight: {
      label: "Weaning Weight (kg)",
      type: "number",
      required: false,
    },
    weaningLocation: {
      label: "Weaning Location/Farm",
      type: "text",
      required: false,
    },
    tickCount: { label: "Tick Count", type: "number", required: false },
    headEvaluation: {
      label: "Head (Evaluation)",
      type: "select",
      required: false,
      options: ["Excellent", "Good", "Fair", "Poor"],
    },
    bodyType: {
      label: "Body Type",
      type: "select",
      required: false,
      options: ["Dairy", "Beef", "Dual Purpose"],
    },
    color: {
      label: "Color",
      type: "select",
      required: false,
      options: ["Black", "White", "Brown", "Black & White", "Red", "Mixed"],
    },
    muscleDevelopment: {
      label: "Muscle Development",
      type: "select",
      required: false,
      options: ["Excellent", "Good", "Fair", "Poor"],
    },
    umbilicalCondition: {
      label: "Umbilical Condition",
      type: "select",
      required: false,
      options: ["Normal", "Infected", "Hernia"],
    },
    bodyStructure: {
      label: "Body Structure",
      type: "select",
      required: false,
      options: ["Excellent", "Good", "Fair", "Poor"],
    },
    generalConformation: {
      label: "General Conformation",
      type: "select",
      required: false,
      options: ["Superior", "Good", "Fair", "Poor"],
    },
    earTagVerified: {
      label: "Ear Tag Verified",
      type: "select",
      required: true,
      options: ["Yes", "No"],
    },
    vaccinations: { label: "vaccinations", type: "checkbox", required: false },
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/animejs/3.2.1/anime.min.js";
    script.onload = () => {
      initializeAnimations();
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const initializeAnimations = () => {
    if (!window.anime) return;

    window.anime({
      targets: ".hero-background",
      opacity: [0, 1],
      scale: [1.1, 1],
      duration: 1200,
      easing: "easeOutCubic",
    });

    window.anime({
      targets: formRef.current,
      opacity: [0, 1],
      translateY: [50, 0],
      scale: [0.95, 1],
      duration: 800,
      delay: 300,
      easing: "easeOutElastic(1, .8)",
    });

    animateFieldsSequence();

    window.anime({
      targets: progressRef.current,
      width: ["0%", "100%"],
      duration: 2000,
      delay: 500,
      easing: "easeInOutCubic",
    });
  };

  const animateFieldsSequence = () => {
    if (!window.anime) return;

    window.anime({
      targets: ".section-card",
      opacity: [0, 1],
      translateY: [30, 0],
      scale: [0.95, 1],
      duration: 600,
      delay: window.anime.stagger(200, { start: 800 }),
      easing: "easeOutBack(1.7)",
    });

    window.anime({
      targets: ".form-field",
      opacity: [0, 1],
      translateX: [-20, 0],
      duration: 400,
      delay: window.anime.stagger(100, { start: 1200 }),
      easing: "easeOutCubic",
    });
  };

  const handleSelectAllVaccines = () => {
    const allVaccines = vaccineArray
      .filter((v) => v.value !== "none")
      .map((v) => v.value);
    setCurrentVaccination(allVaccines);
  };

  const handleDeselectAllVaccines = () => {
    setCurrentVaccination([]);
  };

  const animateFieldFocus = (fieldElement: HTMLElement) => {
    if (!window.anime) return;

    window.anime({
      targets: fieldElement,
      scale: [1, 1.02],
      boxShadow: [
        "0 0 0 0 rgba(59, 130, 246, 0)",
        "0 0 0 4px rgba(59, 130, 246, 0.1)",
      ],
      duration: 200,
      easing: "easeOutCubic",
    });
  };

  const animateFieldBlur = (fieldElement: HTMLElement) => {
    if (!window.anime) return;

    window.anime({
      targets: fieldElement,
      scale: [1.02, 1],
      boxShadow: [
        "0 0 0 4px rgba(59, 130, 246, 0.1)",
        "0 0 0 0 rgba(59, 130, 246, 0)",
      ],
      duration: 200,
      easing: "easeOutCubic",
    });
  };

  const handleVaccinationChange = (vaccineValue: string) => {
    setCurrentVaccination((prev) => {
      if (vaccineValue === "none") {
        return ["none"];
      }

      const filtered = prev.filter((v) => v !== "none");
      if (prev.includes(vaccineValue)) {
        return filtered.filter((v) => v !== vaccineValue);
      } else {
        return [...filtered, vaccineValue];
      }
    });
  };

  const onSubmit = async () => {
    const formData = { ...getValues(), id, vaccinations: currentVaccination };
    const update = mutateAsync(formData);
    toast.promise(update, {
      loading: "Reproductive Data is being submitted",
      success: "Reproductive Data Successfully Submitted.",
      error: "Reproductive Data Failed to Submit.",
    });

    try {
      await update;
      reset(formData);
    } catch {
      // Keep edits available for retry; the mutation reports errors.
    }
  };

  const renderField = (fieldName: keyof typeof fieldDefinitions) => {
    const fieldDef = fieldDefinitions[fieldName];
    const isDirty = !!dirtyFields[fieldName];

    if (!fieldDef) return null;

    if (fieldDef.type === "checkbox" && fieldName === "vaccinations") {
      return (
        <FormItem key={fieldName} className="form-field space-y-3 col-span-full">
          <FormLabel className="field-label text-sm font-semibold text-[#008c8fff] flex items-center flex-wrap gap-2">
            <span>
              {fieldDef.label}
              {fieldDef.required && <span className="text-red-500 ml-1">*</span>}
            </span>
             {isDirty && (
               <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                 Changed
               </span>
             )}
          </FormLabel>
          <div className="flex gap-2 mb-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSelectAllVaccines}
              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
            >
              Select All
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDeselectAllVaccines}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              Deselect All
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 px-2">
            {vaccineArray.map((vaccine) => (
              <div key={vaccine.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`vaccine-${vaccine.value}`}
                  checked={currentVaccination.includes(vaccine.value)}
                  onCheckedChange={() => handleVaccinationChange(vaccine.value)}
                />
                <label
                  htmlFor={`vaccine-${vaccine.value}`}
                  className=" text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {vaccine.label}
                </label>
              </div>
            ))}
          </div>
          {isDirty && (
            <p className="text-[11px] text-amber-600 font-medium mt-1">
              Data has been changed
            </p>
          )}
        </FormItem>
      );
    }

    if (
      fieldDef.type === "select" &&
      "options" in fieldDef &&
      Array.isArray(fieldDef.options)
    ) {
      return (
        <Controller
          key={fieldName}
          name={fieldName}
          control={control}
          render={({ field }) => (
            <FormItem className="space-y-1">
              <FormLabel className="text-sm font-semibold text-[#008c8fff] flex items-center flex-wrap gap-2 mb-2">
                {fieldDef.label}
                {fieldDef.required && <span className="text-red-500">*</span>}
                {isDirty && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <Select
                value={field.value ? String(field.value).toLowerCase() : undefined}
                onValueChange={(value) => {
                  field.onChange(value);
                  trigger(fieldName);
                }}
              >
                <FormControl>
                  <SelectTrigger
                    className={`transition-all duration-200 ${
                      errors[fieldName]
                        ? "border-red-400 focus:border-red-500"
                        : "border-slate-200 focus:border-blue-400"
                    }`}
                    onFocus={(e) =>
                      animateFieldFocus(
                        e.target.closest(".form-field") as HTMLElement,
                      )
                    }
                    onBlur={(e) =>
                      animateFieldBlur(
                        e.target.closest(".form-field") as HTMLElement,
                      )
                    }
                  >
                    <SelectValue
                      placeholder={`Select ${fieldDef.label.toLowerCase()}`}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white ">
                  {fieldDef.options.map((option) => (
                    <SelectItem
                      className={"overflow-y-auto z-50 background-white"}
                      key={option}
                      value={String(option).toLowerCase()}
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
               <div className="min-h-[20px]">
                 {isDirty && (
                   <p className="text-[11px] text-amber-600 font-medium mt-1">
                     Data has been changed
                   </p>
                 )}
               </div>
               <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    if (fieldDef.type === "textarea") {
      return (
        <Controller
          key={fieldName}
          name={fieldName}
          control={control}
          render={({ field }) => (
            <FormItem className="space-y-1">
              <FormLabel className="text-sm font-semibold text-[#008c8fff] flex items-center flex-wrap gap-2 mb-2">
                {fieldDef.label}
                {fieldDef.required && <span className="text-red-500">*</span>}
                {isDirty && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  id={fieldName}
                  placeholder={`Enter ${fieldDef.label.toLowerCase()}`}
                  className={`transition-all duration-200 min-h-[80px] ${
                    errors[fieldName]
                      ? "border-red-400 focus:border-red-500"
                      : "border-slate-200 focus:border-blue-400"
                  }`}
                  onFocus={(e) =>
                    animateFieldFocus(
                      e.target.closest(".form-field") as HTMLElement,
                    )
                  }
                  onBlur={(e) => {
                    animateFieldBlur(
                      e.target.closest(".form-field") as HTMLElement,
                    );
                    trigger(fieldName);
                  }}
                />
              </FormControl>
              <div className="min-h-[20px]">
                 {isDirty && (
                   <p className="text-[11px] text-amber-600 font-medium mt-1">
                     Data has been changed
                   </p>
                 )}
               </div>
               <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <Controller
        key={fieldName}
        name={fieldName}
        control={control}
        render={({ field }) => (
          <FormItem className="space-y-1">
            <FormLabel className="text-sm font-semibold text-[#008c8fff] flex items-center flex-wrap gap-2 mb-2">
              {fieldDef.label}
              {fieldDef.required && <span className="text-red-500">*</span>}
              {isDirty && (
                <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                  Changed
                </span>
              )}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                id={fieldName}
                type={fieldDef.type}
                placeholder={`Enter ${fieldDef.label.toLowerCase()}`}
                className={`transition-all duration-200 ${
                  errors[fieldName]
                    ? "border-red-400 focus:border-red-500"
                    : "border-slate-200 focus:border-blue-400"
                }`}
                onFocus={(e) =>
                  animateFieldFocus(e.target.closest(".form-field") as HTMLElement)
                }
                onBlur={(e) => {
                  animateFieldBlur(e.target.closest(".form-field") as HTMLElement);
                  trigger(fieldName);
                }}
                onChange={(e) => {
                  const value =
                    fieldDef.type === "number"
                      ? Number(e.target.value)
                      : e.target.value;
                  field.onChange(value);
                }}
                aria-describedby={
                  errors[fieldName] ? `${fieldName}-error` : undefined
                }
              />
            </FormControl>
            <div className="min-h-[20px]">
              {isDirty && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };
  if (!id) return <p role="alert">Animal ID is missing.</p>;
  if (isLoading) return <p>Loading reproductive process...</p>;
  if (isError) return <div role="alert">Unable to load reproductive process. <Button onClick={() => refetch()}>Retry</Button></div>;

  return (
    <div className="min-h-screen relative overflow-hidden sm:px-0">
      <div className="hero-background absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-100">
        <div className="absolute inset-0 opacity-40">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%2315803d' fillOpacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>
      </div>

      <div className="fixed top-0 left-0 w-full h-1 bg-slate-200 z-50">
        <div
          ref={progressRef}
          className="h-full bg-gradient-to-r from-emerald-500 to-teal-600"
        ></div>
      </div>

      <div className="relative z-10 max-w-7xl sm:w-full mx-auto lg:p-6">
        <Card
          ref={formRef}
          className="shadow-2xl border-0 bg-slate-100 backdrop-blur-md sm:px-0"
        >
          <CardHeader className="text-center pb-8">
            <CardTitle className="text-4xl font-bold bg-gradient-to-r from-emerald-700 to-teal-900 bg-clip-text text-transparent">
              Comprehensive Cattle Management System
            </CardTitle>
            <CardDescription className="text-lg text-[#015354ff] mt-2">
              Complete cattle lifecycle tracking and management
            </CardDescription>
          </CardHeader>

          <CardContent className={"w-full px-0"}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <FormProvider {...form}>
                {formSections.map((section, sectionIndex) => (
                  <Card
                    key={sectionIndex}
                    className="section-card bg-white/60 backdrop-blur-sm border border-slate-200/50"
                  >
                    <CardHeader className="pb-4">
                      <CardTitle className=" flex items-center space-x-3 text-xl font-semibold text-slate-800">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          {section.icon}
                        </div>
                        <span className={"text-[#008c8fff]"}>
                          {section.title}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="sm:w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {section.fields.map((fieldName) =>
                          renderField(fieldName as keyof typeof fieldDefinitions),
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <div className="pt-8 flex justify-center">
                  <Button
                    type="submit"
                    disabled={isPending || form.formState.isSubmitting || !isValid}
                    className="submit-button px-12 py-4 text-lg font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {isPending ? (
                      <div className="flex items-center space-x-3">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Processing Submission...</span>
                      </div>
                    ) : (
                      "Submit Cattle Information"
                    )}
                  </Button>
                </div>
              </FormProvider>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
